import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

import PDFProcessor from './components/PDFProcessor';
import About from './components/About';
import PrivacyPolicy from './components/PrivacyPolicy.jsx';
import LandingPage from './components/LandingPage';
import Pricing from './components/Pricing';
import SignIn from './components/singinpage.jsx';
import { UserProvider } from './components/UserContext';
import ReferPage from './components/ReferPage';
import BlogList from './components/BlogList';
import BlogEditor from './components/BlogEditor';
import BlogPostView from './components/BlogPostView';
import { AuthProvider } from './components/AuthContext';

function App() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  const glassEffectPrimary =
    'bg-black/30 backdrop-blur-xl border border-gray-800/40';

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen relative overflow-x-hidden bg-black text-white font-poppins">
          {/* Background */}
          <div className="absolute inset-0 z-0 bg-black" />

          {/* Fixed Header */}
          <header
            className={`fixed top-0 left-1/2 -translate-x-1/2 z-20
            ${glassEffectPrimary}
            w-[90%] max-w-5xl
            py-2 sm:py-5
            px-3 sm:px-5
            rounded-3xl
            mt-2`}
          >
            <div className="flex items-center justify-between">
              <h1 className="text-xl sm:text-2xl font-semibold text-gradient text-center">
                NexNotes AI
              </h1>

              {/* Desktop Sign In Button */}
              <Link
                to="/signin"
                className="hidden sm:block px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
              >
                Sign In
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleDrawer}
                className="sm:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
              >
                {isDrawerOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </header>

          {/* Mobile Drawer */}
          <div
            className={`fixed top-0 right-0 h-full w-64 ${glassEffectPrimary} z-30 transform transition-transform duration-300 ease-in-out sm:hidden ${
              isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex flex-col h-full p-6">
              {/* Close button */}
              <button
                onClick={toggleDrawer}
                className="self-end p-2 rounded-lg hover:bg-white/10 transition-colors mb-8"
                aria-label="Close menu"
              >
                <X size={24} />
              </button>

              {/* Drawer Content */}
              <nav className="flex flex-col gap-4">
                <Link
                  to="/signin"
                  onClick={toggleDrawer}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200 text-center"
                >
                  Sign In
                </Link>
                
                {/* You can add more navigation items here */}
                <Link
                  to="/"
                  onClick={toggleDrawer}
                  className="px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-all duration-200 text-center"
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  onClick={toggleDrawer}
                  className="px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-all duration-200 text-center"
                >
                  About
                </Link>
                <Link
                  to="/pricing"
                  onClick={toggleDrawer}
                  className="px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-all duration-200 text-center"
                >
                  Pricing
                </Link>
                <Link
                  to="/blog"
                  onClick={toggleDrawer}
                  className="px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-all duration-200 text-center"
                >
                  Blog
                </Link>
              </nav>
            </div>
          </div>

          {/* Overlay when drawer is open */}
          {isDrawerOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-20 sm:hidden"
              onClick={toggleDrawer}
            />
          )}

          {/* Main Content */}
          <div className="max-w-7xl mx-auto relative z-10 pt-16 sm:pt-20 px-4 sm:px-6 pb-6 sm:pb-8">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/app" element={<PDFProcessor />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/refer" element={<ReferPage />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/create" element={<BlogEditor mode="create" />} />
              <Route path="/blog/edit/:postId" element={<BlogEditor mode="edit" />} />
              <Route path="/blog/post/:postId" element={<BlogPostView />} />
              <Route
                path="*"
                element={
                  <h2 className="text-center text-4xl text-white mt-20">
                    404 - Page Not Found
                  </h2>
                }
              />
            </Routes>
          </div>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;